import { useMemo, useState } from "react";
import { motion } from "framer-motion";

const JODHPUR_BG =
    "https://images.pexels.com/photos/15774210/pexels-photo-15774210.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Star = ({ size = 14, color = "var(--gold)", className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path d="M12 0 C13 8 16 11 24 12 C16 13 13 16 12 24 C11 16 8 13 0 12 C8 11 11 8 12 0 Z" fill={color} />
    </svg>
);

const Flower = ({ size = 44, petal = "#E8B4B8", center = "#D4AF37", className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" className={className} aria-hidden="true">
        {[0, 72, 144, 216, 288].map((a) => (
            <ellipse key={a} cx="20" cy="10.5" rx="6" ry="10.5" fill={petal} opacity="0.9" transform={`rotate(${a} 20 20)`} />
        ))}
        <circle cx="20" cy="20" r="4.6" fill={center} />
    </svg>
);

const rand = (min, max) => min + Math.random() * (max - min);

const PETAL_COLORS = ["#E8B4B8", "#E9C3C7", "#F3D9DC", "#D4AF37", "#DFA0A6"];

export default function EnvelopeOverlay({ onOpenStart, onOpen }) {
    const [opening, setOpening] = useState(false);

    const ambientStars = useMemo(
        () =>
            Array.from({ length: 22 }, (_, i) => ({
                id: i,
                left: rand(4, 95),
                top: rand(5, 93),
                size: rand(7, 17),
                duration: rand(1.8, 3.6),
                delay: rand(0, 2.6),
                color: Math.random() > 0.35 ? "var(--gold)" : "var(--rose)",
            })),
        []
    );

    const fallingPetals = useMemo(
        () =>
            Array.from({ length: 20 }, (_, i) => ({
                id: i,
                left: rand(0, 100),
                size: rand(9, 18),
                duration: rand(9, 16),
                delay: rand(0, 10),
                sway: rand(24, 60),
                rotate: rand(140, 320),
                color: PETAL_COLORS[i % PETAL_COLORS.length],
                opacity: rand(0.5, 0.9),
            })),
        []
    );

    const flowers = useMemo(
        () => [
            { id: "tl", left: "3%", top: "6%", size: 64, petal: "#E8B4B8", blur: 0, duration: 26, opacity: 0.85 },
            { id: "tr", left: "88%", top: "10%", size: 46, petal: "#F3D9DC", blur: 1.2, duration: 32, opacity: 0.7 },
            { id: "bl", left: "5%", top: "78%", size: 54, petal: "#DFA0A6", blur: 0.8, duration: 28, opacity: 0.75 },
            { id: "br", left: "86%", top: "74%", size: 72, petal: "#E8B4B8", blur: 0, duration: 24, opacity: 0.9 },
            { id: "ml", left: "12%", top: "42%", size: 36, petal: "#F3D9DC", blur: 1.6, duration: 34, opacity: 0.6 },
            { id: "mr", left: "80%", top: "46%", size: 40, petal: "#E9C3C7", blur: 1.4, duration: 30, opacity: 0.65 },
        ],
        []
    );

    const burstStars = useMemo(
        () =>
            Array.from({ length: 28 }, (_, i) => {
                const angle = (i / 28) * Math.PI * 2 + rand(-0.2, 0.2);
                const dist = rand(120, 320);
                return {
                    id: i,
                    x: Math.cos(angle) * dist,
                    y: Math.sin(angle) * dist,
                    size: rand(9, 24),
                    rotate: rand(140, 340),
                    delay: rand(0, 0.28),
                    color: ["#D4AF37", "#E8B4B8", "#F3E5AB"][i % 3],
                };
            }),
        []
    );

    const handleOpen = () => {
        if (opening) return;
        setOpening(true);
        onOpenStart?.();
        setTimeout(onOpen, 1900);
    };

    return (
        <motion.div
            data-testid="envelope-overlay"
            className="envelope-overlay"
            exit={{ opacity: 0, transition: { duration: 0.9, ease: "easeInOut" } }}
        >
            <img
                src={JODHPUR_BG}
                alt=""
                aria-hidden="true"
                data-testid="envelope-bg-image"
                className="absolute inset-0 h-full w-full object-cover"
            />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(250,246,240,0.68) 0%, rgba(250,246,240,0.78) 55%, rgba(250,246,240,0.9) 100%)",
                }}
            />

            {fallingPetals.map((p) => (
                <motion.span
                    key={`petal-${p.id}`}
                    className="pointer-events-none absolute -top-10"
                    style={{ left: `${p.left}%`, opacity: p.opacity }}
                    animate={{
                        y: ["-6vh", "108vh"],
                        x: [0, p.sway, -p.sway * 0.6, p.sway * 0.3, 0],
                        rotate: [0, p.rotate],
                    }}
                    transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "linear" }}
                >
                    <svg width={p.size} height={p.size * 1.4} viewBox="0 0 24 34">
                        <path d="M12 1 C19 8 21 18 12 33 C3 18 5 8 12 1 Z" fill={p.color} />
                    </svg>
                </motion.span>
            ))}

            {flowers.map((f) => (
                <motion.span
                    key={`flower-${f.id}`}
                    className="pointer-events-none absolute"
                    style={{ left: f.left, top: f.top, opacity: f.opacity, filter: f.blur ? `blur(${f.blur}px)` : "none" }}
                    animate={{ rotate: 360, y: [0, -10, 0] }}
                    transition={{
                        rotate: { duration: f.duration, repeat: Infinity, ease: "linear" },
                        y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
                    }}
                >
                    <Flower size={f.size} petal={f.petal} />
                </motion.span>
            ))}

            {ambientStars.map((s) => (
                <motion.span
                    key={s.id}
                    className="pointer-events-none absolute"
                    style={{ left: `${s.left}%`, top: `${s.top}%` }}
                    animate={{ scale: [0.3, 1, 0.3], opacity: [0.15, 1, 0.15], rotate: [0, 25, 0] }}
                    transition={{ duration: s.duration, delay: s.delay, repeat: Infinity, ease: "easeInOut" }}
                >
                    <Star size={s.size} color={s.color} className="star-glow" />
                </motion.span>
            ))}

            {opening && (
                <div className="pointer-events-none absolute left-1/2 top-1/2 z-20">
                    <motion.span
                        className="absolute block rounded-full"
                        style={{
                            width: 140,
                            height: 140,
                            left: -70,
                            top: -70,
                            background: "radial-gradient(circle, rgba(212,175,55,0.75), rgba(232,180,184,0.35) 55%, transparent 72%)",
                        }}
                        initial={{ scale: 0, opacity: 0.95 }}
                        animate={{ scale: 3.4, opacity: 0 }}
                        transition={{ duration: 1, delay: 0.1, ease: "easeOut" }}
                    />
                    {burstStars.map((s) => (
                        <motion.span
                            key={s.id}
                            className="absolute"
                            initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                            animate={{ x: s.x, y: s.y, scale: [0, 1.35, 0.15], opacity: [1, 1, 0], rotate: s.rotate }}
                            transition={{ duration: 1.2, delay: 0.15 + s.delay, ease: "easeOut" }}
                        >
                            <Star size={s.size} color={s.color} className="star-glow" />
                        </motion.span>
                    ))}
                </div>
            )}

            <motion.p
                className="font-hindi relative z-10 text-lg sm:text-xl"
                style={{ color: "var(--wine)" }}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
            >
                ॐ श्री गणेशाय नमः
            </motion.p>

            <motion.div
                className="envelope-scene relative z-10"
                data-testid="envelope-scene"
                initial={{ opacity: 0, y: 40, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
                <motion.div
                    className="envelope"
                    animate={opening ? {} : { y: [0, -6, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                    <div className="envelope-body" />
                    <motion.div
                        className="envelope-letter"
                        animate={opening ? { y: "-58%", transition: { delay: 0.55, duration: 0.8, ease: [0.22, 1, 0.36, 1] } } : {}}
                    >
                        <span className="font-display text-3xl" style={{ color: "var(--wine)" }}>
                            V ♡ D
                        </span>
                        <span className="text-[0.65rem] uppercase tracking-[0.3em]" style={{ color: "var(--wine)" }}>
                            24 · 11 · 2026
                        </span>
                    </motion.div>
                    <div className="envelope-pocket" />
                    <motion.div
                        className="envelope-flap"
                        animate={opening ? { rotateX: 180, zIndex: 1 } : {}}
                        transition={{ duration: 0.7, ease: "easeInOut" }}
                    >
                        <div className="envelope-flap-inner" />
                    </motion.div>
                    <motion.button
                        type="button"
                        data-testid="open-envelope-button"
                        aria-label="Tap the seal to open the invitation"
                        onClick={handleOpen}
                        className="wax-seal cursor-pointer"
                        style={{ x: "-50%", y: "-50%" }}
                        animate={opening ? { scale: 0, opacity: 0, transition: { duration: 0.35 } } : {}}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        {!opening && <span className="seal-pulse" />}
                        <span className="font-display text-lg tracking-wide" style={{ color: "#f8e7c9" }}>
                            V&amp;D
                        </span>
                    </motion.button>
                </motion.div>
            </motion.div>

            <motion.div
                className="relative z-10 flex flex-col items-center gap-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
            >
                {opening ? (
                    <p className="text-sm" style={{ color: "var(--muted)" }}>
                        Opening your invitation…
                    </p>
                ) : (
                    <p
                        className="float-slow text-sm font-semibold uppercase tracking-[0.25em]"
                        style={{ color: "var(--wine)" }}
                        data-testid="seal-hint"
                    >
                        Open the envelope
                    </p>
                )}
            </motion.div>
        </motion.div>
    );
}
